/**
 * 2D affine math for page layout and the world → paper mapping.
 *
 * A {@link Matrix} `[a, b, c, d, e, f]` maps `(x, y)` to
 * `(a·x + c·y + e, b·x + d·y + f)` — the SVG / PDF `cm` convention, so it can
 * be handed to either backend unchanged.
 */

export type Matrix = readonly [number, number, number, number, number, number];

export interface Pt {
  x: number;
  y: number;
}

export const IDENTITY: Matrix = [1, 0, 0, 1, 0, 0];

/** `outer ∘ inner`: apply `inner` first, then `outer`. */
export function multiply(outer: Matrix, inner: Matrix): Matrix {
  const [a1, b1, c1, d1, e1, f1] = outer;
  const [a2, b2, c2, d2, e2, f2] = inner;
  return [a1 * a2 + c1 * b2, b1 * a2 + d1 * b2, a1 * c2 + c1 * d2, b1 * c2 + d1 * d2, a1 * e2 + c1 * f2 + e1, b1 * e2 + d1 * f2 + f1];
}

/** Composes left to right: `compose(A, B, C)` applies C, then B, then A. */
export function compose(...matrices: Matrix[]): Matrix {
  return matrices.reduce((acc, m) => multiply(acc, m), IDENTITY);
}

export function apply(m: Matrix, x: number, y: number): Pt {
  return { x: m[0] * x + m[2] * y + m[4], y: m[1] * x + m[3] * y + m[5] };
}

/** Applies only the linear part (directions, not positions). */
export function applyVector(m: Matrix, x: number, y: number): Pt {
  return { x: m[0] * x + m[2] * y, y: m[1] * x + m[3] * y };
}

export function translate(x: number, y: number): Matrix {
  return [1, 0, 0, 1, x, y];
}

export function scale(sx: number, sy: number = sx): Matrix {
  return [sx, 0, 0, sy, 0, 0];
}

/**
 * Rotation by `deg` from +x towards +y. On a y-down page that reads
 * clockwise; in y-up world coordinates it reads counter-clockwise.
 */
export function rotate(deg: number): Matrix {
  const r = (deg * Math.PI) / 180;
  const cos = Math.cos(r);
  const sin = Math.sin(r);
  return [cos, sin, -sin, cos, 0, 0];
}

export function isIdentity(m: Matrix): boolean {
  return m.every((v, i) => Math.abs(v - IDENTITY[i]) < 1e-12);
}

/** Rotation/translation only — stroke widths and text sizes pass through unchanged. */
export function isRigid(m: Matrix, eps = 1e-9): boolean {
  const [a, b, c, d] = m;
  return Math.abs(a * a + b * b - 1) < eps && Math.abs(c * c + d * d - 1) < eps && Math.abs(a * c + b * d) < eps;
}

export function degrees(rad: number): number {
  return (rad * 180) / Math.PI;
}

// ─── Arcs ───────────────────────────────────────────────────────────────

export interface CubicSegment {
  c1: Pt;
  c2: Pt;
  to: Pt;
}

/** Cubic Béziers of a circular arc around `center`, `sweep` radians (sign = direction), at most 90° each. */
export function arcCubics(center: Pt, radius: number, startAngle: number, sweep: number): CubicSegment[] {
  const count = Math.max(1, Math.ceil(Math.abs(sweep) / (Math.PI / 2) - 1e-9));
  const step = sweep / count;
  const k = (4 / 3) * Math.tan(step / 4);
  const out: CubicSegment[] = [];
  for (let i = 0; i < count; i++) {
    const a0 = startAngle + i * step;
    const a1 = a0 + step;
    const p0 = { x: center.x + radius * Math.cos(a0), y: center.y + radius * Math.sin(a0) };
    const p3 = { x: center.x + radius * Math.cos(a1), y: center.y + radius * Math.sin(a1) };
    out.push({
      c1: { x: p0.x - k * radius * Math.sin(a0), y: p0.y + k * radius * Math.cos(a0) },
      c2: { x: p3.x + k * radius * Math.sin(a1), y: p3.y - k * radius * Math.cos(a1) },
      to: p3,
    });
  }
  return out;
}

/**
 * The arc a DXF bulge describes from `p0` to `p1` (y-up coordinates; positive
 * bulge = counter-clockwise, bulge = tan(sweep / 4)), as cubics in the same
 * coordinates. Affine maps keep Bézier control points exact, so map the
 * result afterwards. A (near-)zero bulge yields no curve: draw a line.
 */
export function bulgeCubics(p0: Pt, p1: Pt, bulge: number): CubicSegment[] {
  const dx = p1.x - p0.x;
  const dy = p1.y - p0.y;
  const chord = Math.hypot(dx, dy);
  if (Math.abs(bulge) < 1e-9 || chord < 1e-12) return [];
  const sweep = 4 * Math.atan(bulge);
  // Center sits on the chord's perpendicular bisector; left of p0→p1 for a CCW arc under a semicircle.
  const offset = ((chord / 2) * (1 - bulge * bulge)) / (2 * bulge);
  const center = { x: (p0.x + p1.x) / 2 - (dy / chord) * offset, y: (p0.y + p1.y) / 2 + (dx / chord) * offset };
  const radius = Math.hypot(p0.x - center.x, p0.y - center.y);
  const segments = arcCubics(center, radius, Math.atan2(p0.y - center.y, p0.x - center.x), sweep);
  // Land exactly on p1 (no accumulated float drift at the joint).
  segments[segments.length - 1].to = { x: p1.x, y: p1.y };
  return segments;
}
